import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    AvailableSubscriptionPlanResponse,
    SubscriptionBillingStateResponse,
    SubscriptionInvoiceResponse,
    cancelPaidSubscription,
    continuePaidSubscription,
    createCustomerPortalSession,
    createPaidSubscription,
    getApiErrorMessage,
    getApiResponseMessage,
    getAvailableSubscriptionPlans,
    getCurrentSubscriptionBillingState,
    getUserSubscriptionInvoices,
} from '../../services/subscriptionPaymentService';
import { RootState } from '../store';
import { fetchUserProfileInfo } from '../profile-slice/profile-slice';

export type SubscriptionActionInProgress = 'buy' | 'change' | 'cancel' | 'continue' | null;

interface LoadRequest {
    showLoader?: boolean;
}

interface SubscriptionPortalRequest {
    returnUrl: string;
}

interface SubscriptionCheckoutRequest {
    stripePriceId: string;
    subscriptionName: string;
    returnUrl: string;
}

interface SubscriptionActionRequest {
    localSubscriptionId: string;
}

interface SubscriptionState {
    billingState: SubscriptionBillingStateResponse | null;
    availablePlans: AvailableSubscriptionPlanResponse[];
    invoices: SubscriptionInvoiceResponse[];
    loadingBillingState: boolean;
    loadingPlans: boolean;
    loadingInvoices: boolean;
    openingPortal: boolean;
    subscriptionActionInProgress: SubscriptionActionInProgress;
    subscriptionSyncPending: boolean;
}

const initialState: SubscriptionState = {
    billingState: null,
    availablePlans: [],
    invoices: [],
    loadingBillingState: false,
    loadingPlans: false,
    loadingInvoices: false,
    openingPortal: false,
    subscriptionActionInProgress: null,
    subscriptionSyncPending: false,
};

export const fetchSubscriptionBillingState = createAsyncThunk<
    SubscriptionBillingStateResponse | null,
    LoadRequest | undefined,
    { rejectValue: string }
>('subscription/fetchSubscriptionBillingState', async (_, thunkAPI) => {
    try {
        const response = await getCurrentSubscriptionBillingState();
        if (!response.success) {
        return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        return response.result ?? null;
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const fetchSubscriptionPlans = createAsyncThunk<
    AvailableSubscriptionPlanResponse[],
    LoadRequest | undefined,
    { rejectValue: string }
>('subscription/fetchSubscriptionPlans', async (_, thunkAPI) => {
    try {
        const response = await getAvailableSubscriptionPlans();
        if (!response.success) {
            return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        return response.result ?? [];
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const fetchSubscriptionInvoices = createAsyncThunk<
    SubscriptionInvoiceResponse[],
    LoadRequest | undefined,
    { rejectValue: string }
>('subscription/fetchSubscriptionInvoices', async (_, thunkAPI) => {
    try {
        const response = await getUserSubscriptionInvoices();
        if (!response.success) {
            return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        return response.result ?? [];
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const refreshSubscriptionSection = createAsyncThunk<void, LoadRequest | undefined>(
    'subscription/refreshSubscriptionSection',
    async (request, thunkAPI) => {
        const args = { showLoader: request?.showLoader ?? false };
        await Promise.all([
            thunkAPI.dispatch(fetchUserProfileInfo()),
            thunkAPI.dispatch(fetchSubscriptionBillingState(args)),
            thunkAPI.dispatch(fetchSubscriptionInvoices(args)),
            thunkAPI.dispatch(fetchSubscriptionPlans(args)),
        ]);
    }
);

export const openCustomerPortalSession = createAsyncThunk<
    string,
    SubscriptionPortalRequest,
    { rejectValue: string }
>('subscription/openCustomerPortalSession', async (request, thunkAPI) => {
    try {
        const response = await createCustomerPortalSession({ returnUrl: request.returnUrl });
        if (!response.success || !response.result?.portalUrl) {
            return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        return response.result.portalUrl;
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const createSubscriptionCheckoutSession = createAsyncThunk<
    string,
    SubscriptionCheckoutRequest,
    { rejectValue: string }
>('subscription/createSubscriptionCheckoutSession', async (request, thunkAPI) => {
    try {
        const response = await createPaidSubscription({
            successUrl: request.returnUrl,
            cancelUrl: request.returnUrl,
            stripePriceId: request.stripePriceId,
            subscriptionName: request.subscriptionName,
        });

        if (!response.success || !response.result?.checkoutUrl) {
            return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        return response.result.checkoutUrl;
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const cancelSubscriptionAtPeriodEnd = createAsyncThunk<
    SubscriptionBillingStateResponse | null,
    SubscriptionActionRequest,
    { rejectValue: string }
>('subscription/cancelSubscriptionAtPeriodEnd', async (request, thunkAPI) => {
    try {
        const response = await cancelPaidSubscription({ localSubscriptionId: request.localSubscriptionId });
        if (!response.success) {
            return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        return response.result ?? null;
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const continueSubscriptionRenewal = createAsyncThunk<
    SubscriptionBillingStateResponse | null,
    SubscriptionActionRequest,
    { rejectValue: string }
>('subscription/continueSubscriptionRenewal', async (request, thunkAPI) => {
    try {
        const response = await continuePaidSubscription({ localSubscriptionId: request.localSubscriptionId });
        if (!response.success) {
            return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        return response.result ?? null;
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const subscriptionSlice = createSlice({
    name: 'subscription',
    initialState,
    reducers: {
        setSubscriptionSyncPending: (state, action: PayloadAction<boolean>) => {
            state.subscriptionSyncPending = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSubscriptionBillingState.pending, (state, action) => {
            if (action.meta.arg?.showLoader ?? false) {
                state.loadingBillingState = true;
            }
        });
        builder.addCase(fetchSubscriptionBillingState.fulfilled, (state, action) => {
            state.loadingBillingState = false;
            state.billingState = action.payload;
        });
        builder.addCase(fetchSubscriptionBillingState.rejected, (state) => {
            state.loadingBillingState = false;
            state.billingState = null;
        });

        builder.addCase(fetchSubscriptionPlans.pending, (state, action) => {
            if (action.meta.arg?.showLoader ?? false) {
                state.loadingPlans = true;
            }
        });
        builder.addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
            state.loadingPlans = false;
            state.availablePlans = action.payload;
        });
        builder.addCase(fetchSubscriptionPlans.rejected, (state) => {
            state.loadingPlans = false;
            state.availablePlans = [];
        });

        builder.addCase(fetchSubscriptionInvoices.pending, (state, action) => {
            if (action.meta.arg?.showLoader ?? false) {
                state.loadingInvoices = true;
            }
        });
        builder.addCase(fetchSubscriptionInvoices.fulfilled, (state, action) => {
            state.loadingInvoices = false;
            state.invoices = action.payload;
        });
        builder.addCase(fetchSubscriptionInvoices.rejected, (state) => {
            state.loadingInvoices = false;
            state.invoices = [];
        });

        builder.addCase(openCustomerPortalSession.pending, (state) => {
            state.openingPortal = true;
        });
        builder.addCase(openCustomerPortalSession.fulfilled, (state) => {
            state.openingPortal = false;
        });
        builder.addCase(openCustomerPortalSession.rejected, (state) => {
            state.openingPortal = false;
        });

        builder.addCase(createSubscriptionCheckoutSession.pending, (state) => {
            state.subscriptionActionInProgress = 'buy';
        });
        builder.addCase(createSubscriptionCheckoutSession.fulfilled, (state) => {
            state.subscriptionActionInProgress = null;
        });
        builder.addCase(createSubscriptionCheckoutSession.rejected, (state) => {
            state.subscriptionActionInProgress = null;
        });

        builder.addCase(cancelSubscriptionAtPeriodEnd.pending, (state) => {
            state.subscriptionActionInProgress = 'cancel';
        });
        builder.addCase(cancelSubscriptionAtPeriodEnd.fulfilled, (state, action) => {
            state.subscriptionActionInProgress = null;
            if (action.payload) {
                state.billingState = {
                    ...action.payload,
                    isCancelAtPeriodEnd: true,
                    cancelsAtUtc: action.payload.endDateUtc ?? action.payload.cancelsAtUtc ?? null,
                };
            } else if (state.billingState) {
                state.billingState = {
                    ...state.billingState,
                    isCancelAtPeriodEnd: true,
                    cancelsAtUtc: state.billingState.endDateUtc ?? state.billingState.cancelsAtUtc ?? null,
                };
            }
        });
        builder.addCase(cancelSubscriptionAtPeriodEnd.rejected, (state) => {
            state.subscriptionActionInProgress = null;
        });

        builder.addCase(continueSubscriptionRenewal.pending, (state) => {
            state.subscriptionActionInProgress = 'continue';
        });
        builder.addCase(continueSubscriptionRenewal.fulfilled, (state, action) => {
            state.subscriptionActionInProgress = null;
            if (action.payload) {
                state.billingState = {
                    ...action.payload,
                    isCancelAtPeriodEnd: false,
                    cancelsAtUtc: null,
                };
            } else if (state.billingState) {
                state.billingState = {
                    ...state.billingState,
                    isCancelAtPeriodEnd: false,
                    cancelsAtUtc: null,
                };
            }
        });
        builder.addCase(continueSubscriptionRenewal.rejected, (state) => {
            state.subscriptionActionInProgress = null;
        });
    },
});

export const { setSubscriptionSyncPending } = subscriptionSlice.actions;

export const subscriptionSelector = (state: RootState) => state.subscription;

export const selectHasActivePaidSubscription = (state: RootState) =>
    state.subscription.billingState?.hasActivePaidSubscription === true;

export const selectHasActiveTrialSubscription = (state: RootState) =>
    state.subscription.billingState?.hasActiveTrialSubscription === true;

export const selectIsCancellationScheduled = (state: RootState) =>
    state.subscription.billingState?.isCancelAtPeriodEnd === true;

export const selectEffectiveLocalSubscriptionId = (state: RootState) =>
    state.subscription.billingState?.localSubscriptionId || state.profile.userInfo?.currentSubscription?.id || null;

export const selectHasBillingSubscriptionState = (state: RootState) => {
    const currentSubscription = state.profile.userInfo?.currentSubscription ?? null;
    const billingState = state.subscription.billingState;

    const hasStripeBillingIndicator =
        !!billingState?.localSubscriptionId ||
        !!billingState?.trialEndsAtUtc ||
        !!billingState?.cancelsAtUtc;

    return (
        billingState?.hasActivePaidSubscription === true ||
        billingState?.hasActiveTrialSubscription === true ||
        hasStripeBillingIndicator ||
        (!!currentSubscription && currentSubscription.isActiveSubscription === true)
    );
};

export const selectCanCancelPaidSubscription = (state: RootState) => {
    const localSubscriptionId = selectEffectiveLocalSubscriptionId(state);
    const hasCancelableSubscription =
        selectHasActivePaidSubscription(state) ||
        selectHasActiveTrialSubscription(state);

    return (
        hasCancelableSubscription &&
        !selectIsCancellationScheduled(state) &&
        !!localSubscriptionId
    );
};

export const selectCanContinuePaidSubscription = (state: RootState) => {
    const localSubscriptionId = selectEffectiveLocalSubscriptionId(state);
    const hasCancelableSubscription =
        selectHasActivePaidSubscription(state) ||
        selectHasActiveTrialSubscription(state);

    return (
        hasCancelableSubscription &&
        selectIsCancellationScheduled(state) &&
        !!localSubscriptionId
    );
};

export const selectHasAnyActiveSubscription = (state: RootState) => {
    const billingState = state.subscription.billingState;
    const localCurrentSubscription = state.profile.userInfo?.currentSubscription ?? state.auth.currentSubscription;

    return (
        billingState?.hasActivePaidSubscription === true ||
        billingState?.hasActiveTrialSubscription === true ||
        localCurrentSubscription?.isActiveSubscription === true
    );
};
